import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook that provides group-aware navigation functions
 */
export const useGroupNavigation = () => {
  const navigate = useNavigate();
  const { groupSlug } = useParams<{ groupSlug: string }>();
  const { currentGroupSlug } = useAuth();

  // Use the group slug from URL if available, otherwise fall back to context
  const activeGroupSlug = groupSlug || currentGroupSlug;

  /**
   * Navigate to a path within the current group context
   */
  const navigateToGroup = (path: string, options?: { replace?: boolean }) => {
    if (!activeGroupSlug) {
      console.warn('No active group slug available for navigation');
      navigate('/group-selection', options);
      return;
    }

    const fullPath = `/${activeGroupSlug}${path}`;
    navigate(fullPath, options);
  };

  /**
   * Navigate to dashboard of current group
   */
  const navigateToDashboard = (options?: { replace?: boolean }) => {
    navigateToGroup('/dashboard', options);
  };

  /**
   * Navigate to my lists page of current group
   */
  const navigateToMyLists = (options?: { replace?: boolean }) => {
    navigateToGroup('/my-lists', options);
  };

  /**
   * Navigate to browse lists page of current group
   */
  const navigateToBrowseLists = (options?: { replace?: boolean }) => {
    navigateToGroup('/browse-lists', options);
  };

  /**
   * Navigate to my reservations page of current group
   */
  const navigateToMyReservations = (options?: { replace?: boolean }) => {
    navigateToGroup('/my-reservations', options);
  };

  /**
   * Navigate to admin page of current group
   */
  const navigateToAdmin = (options?: { replace?: boolean }) => {
    navigateToGroup('/admin', options);
  };

  /**
   * Get the full URL for a path within the current group
   */
  const getGroupPath = (path: string): string => {
    if (!activeGroupSlug) {
      return '/group-selection';
    }
    return `/${activeGroupSlug}${path}`;
  };

  return {
    activeGroupSlug,
    navigateToGroup,
    navigateToDashboard,
    navigateToMyLists,
    navigateToBrowseLists,
    navigateToMyReservations,
    navigateToAdmin,
    getGroupPath
  };
};